/**
 * JsonTreeView — recursive collapsible JSON tree viewer.
 *
 * Usage:
 *   <JsonTreeView data={jsonObject} />
 *
 * Tree starts fully collapsed (only root-level keys visible).
 */

import { useState, useCallback } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ─── Helpers ──────────────────────────────────────────────────────

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const isArray = (v: unknown): v is unknown[] => Array.isArray(v);

const formatValue = (v: unknown): string => {
  if (v === null) {
    return 'null';
  }
  if (v === undefined) {
    return 'undefined';
  }
  if (typeof v === 'string') {
    return `"${v}"`;
  }
  if (typeof v === 'number') {
    return String(v);
  }
  if (typeof v === 'boolean') {
    return String(v);
  }
  return '';
};

// ─── Color helpers ────────────────────────────────────────────────

const valueColor = (v: unknown, theme: Theme): string => {
  if (v === null || v === undefined) {
    return theme.palette.text.secondary;
  }
  if (typeof v === 'string') {
    return theme.palette.success.light;
  }
  if (typeof v === 'number') {
    return theme.palette.info.main;
  }
  if (typeof v === 'boolean') {
    return theme.palette.secondary.light;
  }
  return theme.palette.text.disabled;
};

// ─── Tree Node ────────────────────────────────────────────────────

interface TreeNodeProps {
  keyLabel: string;
  value: unknown;
  depth: number;
  defaultOpen?: boolean;
  childrenDefaultOpen?: boolean;
}

const TreeNode = ({
  keyLabel,
  value,
  depth,
  defaultOpen = false,
  childrenDefaultOpen = false,
}: TreeNodeProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  const hasChildren = isObject(value) || isArray(value);
  const indent = depth * 16;

  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  // ── text_output special rendering: collapsible formatted display text ──
  if (keyLabel === 'text_output' && typeof value === 'string') {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: `${indent}px`,
            py: '1px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }}
          onClick={toggle}
        >
          <IconButton
            data-ph-capture-attribute-element="moviefone-json-node-expand"
            size="small"
            sx={{
              p: 0,
              mr: 0.25,
              color: theme.palette.text.secondary,
              transition: 'transform 0.15s',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography
            component="span"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '12px',
              fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
              mr: 0.5,
            }}
          >
            {keyLabel}:
          </Typography>
          <Typography
            component="span"
            sx={{
              color: theme.palette.text.primary,
              fontSize: '12px',
              fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
              opacity: 0.6,
            }}
          >
            {value.length} chars
          </Typography>
        </Box>
        {open && (
          <Box sx={{ pl: `${indent + 8}px`, py: 1 }}>
            <pre
              style={{
                margin: 0,
                fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                fontSize: '12px',
                lineHeight: '17px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.default,
                padding: '12px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {value}
            </pre>
          </Box>
        )}
      </Box>
    );
  }

  // ── Primitive leaf ──
  if (!hasChildren) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: `${indent + 8}px`,
          py: '1px',
          fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
          fontSize: '12px',
          lineHeight: '20px',
        }}
      >
        <Typography
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '12px',
            mr: 1,
            fontFamily: 'inherit',
            textAlign: 'right',
            minWidth: '10ex',
          }}
        >
          {keyLabel}:
        </Typography>
        <Typography
          component="span"
          sx={{
            color: valueColor(value, theme),
            fontSize: '12px',
            fontFamily: 'inherit',
          }}
        >
          {formatValue(value)}
        </Typography>
      </Box>
    );
  }

  // ── Object or Array ──
  const rawEntries = isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value);
  // Sort: text_output at the bottom, everything else alphabetically
  const entries = rawEntries.sort((a, b) => {
    if (a[0] === 'text_output') {
      return 1;
    }
    if (b[0] === 'text_output') {
      return -1;
    }
    return a[0].localeCompare(b[0]);
  });

  return (
    <Box>
      {/* Header row — clickable to expand/collapse */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: `${indent}px`,
          py: '1px',
          cursor: 'pointer',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
        onClick={toggle}
      >
        <IconButton
          data-ph-capture-attribute-element="moviefone-json-node-expand"
          size="small"
          sx={{
            p: 0,
            mr: 0.25,
            color: theme.palette.text.secondary,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '12px',
            fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
            mr: 0.5,
          }}
        >
          {keyLabel}:
        </Typography>
        <Typography
          component="span"
          sx={{
            color: theme.palette.text.primary,
            fontSize: '12px',
            fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
            opacity: 0.6,
          }}
        >
          {isArray(value)
            ? `[${value.length}]`
            : `{${Object.keys(value as object).length}}`}
        </Typography>
      </Box>

      {/* Children — all collapsed by default */}
      {open && (
        <Box
          sx={{
            borderLeft: `1px solid ${theme.palette.divider}`,
            ml: `${indent + 8}px`,
          }}
        >
          {entries.map(([k, v]) => {
            if (isArray(value) || isObject(value)) {
              return (
                <TreeNode
                  key={k}
                  keyLabel={k}
                  value={v}
                  depth={depth + 1}
                  defaultOpen={childrenDefaultOpen}
                  childrenDefaultOpen={childrenDefaultOpen}
                />
              );
            }
            return null;
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Root component ───────────────────────────────────────────────

interface JsonTreeViewProps {
  data: unknown;
  childrenDefaultOpen?: boolean;
}

export default function JsonTreeView({
  data,
  childrenDefaultOpen,
}: JsonTreeViewProps) {
  const theme = useTheme();

  if (data === null || data === undefined) {
    return (
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          fontStyle: 'italic',
          p: 1,
          fontSize: '12px',
        }}
      >
        No data
      </Typography>
    );
  }

  if (!isObject(data) && !isArray(data)) {
    return (
      <Typography
        sx={{
          color: valueColor(data, theme),
          fontFamily: 'monospace',
          p: 1,
          fontSize: '12px',
        }}
      >
        {formatValue(data)}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
        fontSize: '12px',
        lineHeight: '20px',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '4px',
        border: `1px solid ${theme.palette.divider}`,
        flex: 1,
        minHeight: 0,
        pb: '1em',
        wordBreak: 'break-word',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.action.disabled,
          borderRadius: '6px',
          border: '3px solid transparent',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      {/* Root is open so we see top-level keys (symbol, regime, etc.) */}
      <TreeNode
        keyLabel="root"
        value={data}
        depth={0}
        defaultOpen
        childrenDefaultOpen={childrenDefaultOpen}
      />
    </Box>
  );
}
